import { useState } from "react";
import { Typography, Button, Tabs, Tab, Link } from "@mui/material";
import { tss } from "tss-react";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { Scrollbars } from "react-custom-scrollbars";
import ModalWindow from "components/ModalWindow";
import ApiUtils from "api/Utils";
import commonStyles from "styles/common";
import { rawEntities } from "constants/common";

const useStyles = tss.create({
    title: {
        fontSize: 16,
        fontWeight: "bold",
    },
    row: {
        position: "relative",
        top: 6,
        width: 371,
        height: 75,
        padding: "7px 12px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
    header: {
        fontSize: 12,
        color: "rgba(0,0,0,0.55)",
    },
    margin: {
        marginTop: -5,
    },
    formControl: {
        margin: 1,
        minWidth: 120,
    },
    button: {
        minWidth: 20,
        padding: "5px 5px",
        marginLeft: 5,
        width: 75,
        height: 36,
    },
    buttonDownload: {
        width: 90,
    },
    bottom: {
        position: "absolute",
        top: "calc(100% - 49px)",
        right: 22,
    },
    nextButton: {
        fontWeight: "bold",
    },
    cancelButton: {
        right: 14,
        fontWeight: "bold",
    },
    select: {
        width: 350,
    },
    dataset: {
        marginTop: 20,
    },
    tabs: {
        width: "100%",
        marginTop: 0,
        marginBottom: 40,
    },
    firstCol: {
        width: 80,
        textAlign: "left",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        cursor: "pointer",
    },
    td: {
        height: 40,
    },
    content: {
        padding: 30,
    },
    table: {},
    textField: {
        width: 300,
    },
    middle: {
        verticalAlign: "middle",
    },
    inner: {
        position: "relative",
        top: "7px",
    },
});

type IProps = {
    open: boolean;
    catalog: any;
    handleClose: () => void;
};

export default function PropertiesPanel(props: IProps) {
    const { classes: common } = commonStyles();
    const { classes, cx } = useStyles();
    const { open, catalog, handleClose } = props;
    const [tab, setTab] = useState(0);
    const [lastEntityID, setlastEntityID] = useState("");
    const [isDownloadingFiles, setIsDownloadingFiles] = useState(false);
    const [filesList, setFilesList] = useState<string[]>([]);
    const handleChange = (e: any, newTab: number) => {
        setTab(newTab);
    };
    const entity = catalog && catalog.entity ? catalog.entity : catalog;

    const copyToClipboard = (url: string) => {
        navigator.clipboard.writeText(url);
    };

    const FilesTabDataHandler = async () => {
        if (lastEntityID !== entity.ID) {
            setIsDownloadingFiles(true);
            const list: any = await ApiUtils.getFilesList(entity.project, entity.ID);
            const splittedList = list.split("\n");
            const arList: string[] = [];
            splittedList.forEach((file: string) => {
                if (file !== "") {
                    arList.push(file);
                }
            });
            setlastEntityID(entity.ID);
            setFilesList(arList);
            setIsDownloadingFiles(false);
        }
    };

    const handleModalClose = () => {
        setTab(0);
        setFilesList([]);
        setlastEntityID("");
        handleClose();
    };

    const modify = () => {};
    return (
        <div>
            {open ? (
                <ModalWindow open={true} handleClose={handleModalClose} onFocus={FilesTabDataHandler} width={700}>
                    <Typography className={classes.title}>Properties</Typography>
                    <div className={classes.dataset}>
                        <Tabs
                            value={tab}
                            onChange={handleChange}
                            aria-label="simple tabs example"
                            indicatorColor="primary"
                            className={classes.tabs}
                        >
                            <Tab label="General" className={common.tab} />
                            <Tab label="Inputs" className={common.tab} />
                            <Tab label="Coordinates" className={common.tab} />
                            {rawEntities.includes(entity.file_type) ? <Tab label="Files" /> : ""}
                        </Tabs>
                        {tab === 0 ? (
                            <div role="tabpanel" className={cx(common.middle, classes.tabs)}>
                                <Scrollbars
                                    autoHide
                                    autoHeight
                                    autoHeightMax={"calc(100vh - 300px)"}
                                    style={{ overflowX: "hidden", width: 700, marginTop: -20 }}
                                >
                                    <table className={classes.table}>
                                        <tbody>
                                            {Object.getOwnPropertyNames(entity).map(
                                                (propertyName: string, index: number) => (
                                                    <tr key={"prop-" + index}>
                                                        <td valign="middle" className={classes.td}>
                                                            <Typography>{propertyName}</Typography>
                                                        </td>
                                                        <td valign="middle">
                                                            <Typography>{entity[propertyName]}</Typography>
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </Scrollbars>
                            </div>
                        ) : tab === 1 ? (
                            <div className={classes.content}>There are no properties in this section</div>
                        ) : tab === 3 ? (
                            isDownloadingFiles ? (
                                <div className={classes.content}>Please wait...</div>
                            ) : filesList.length === 0 ? (
                                <div className={classes.content}>There are no file attachments</div>
                            ) : (
                                <div>
                                    <table className={classes.table}>
                                        <tbody>
                                            {filesList.map((file: any) => {
                                                const fileUrl = ApiUtils.getFileURL(
                                                    entity.project,
                                                    entity.ID,
                                                    file,
                                                    "application/octet-stream"
                                                );
                                                return (
                                                    <tr key={file}>
                                                        <td className={cx(common.mediumLabel, classes.td)}>
                                                            <Typography>{file}</Typography>
                                                        </td>
                                                        <td className={cx(common.mediumLabel, classes.td)}>
                                                            <div className={classes.middle}>
                                                                <Link
                                                                    underline="always"
                                                                    className={cx(
                                                                        common.mediumLabel,
                                                                        common.pointer,
                                                                        classes.inner
                                                                    )}
                                                                    onClick={() => {
                                                                        copyToClipboard(fileUrl);
                                                                    }}
                                                                >
                                                                    <FileCopyIcon />
                                                                </Link>
                                                                <Button
                                                                    href={fileUrl}
                                                                    className={(classes.button, classes.buttonDownload)}
                                                                >
                                                                    Download
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )
                        ) : (
                            <div className={classes.content}>There are no properties in this section</div>
                        )}
                    </div>
                    <div className={classes.bottom}>
                        <Button
                            onClick={(e) => {
                                handleClose();
                            }}
                            aria-label="Cancel"
                            title="Cancel"
                            className={[classes.button, classes.cancelButton].join(" ")}
                        >
                            CANCEL
                        </Button>
                        <Button
                            onClick={(e) => {
                                modify();
                            }}
                            aria-label="Add Selected"
                            title="Add Selected"
                            className={[classes.button, classes.nextButton].join(" ")}
                        >
                            Modify
                        </Button>
                    </div>
                </ModalWindow>
            ) : (
                ""
            )}
        </div>
    );
}
